import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { eventsApi } from '@/features/events/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { dateInputToIsoStart, dateInputToIsoEnd } from '@/features/project/planner/dates';
import { Collapsible } from '@/components/ui/Collapsible';
import { DatePicker } from '@/components/ui/DatePicker';
import { toast } from '@/components/ui/Toast';
import { AdvancedScaffold, type ScaffoldPlan } from '@/features/events/AdvancedScaffold';
import { useT } from '@/lib/i18n/useT';
import { firstInvalidControl } from '@/lib/i18n/nativeValidation';
import type { XkEventGroup } from '@/lib/api/types';

/**
 * Create-project form body, decoupled from any modal shell so it can render
 * inside the right-panel form host (see FormPanelHost). A project maps to an
 * `xk-event-group`. On success it invalidates the brand's groups and calls
 * `onCreated`; the host owns the surrounding header/close chrome.
 */
export function ProjectForm({
  brandId,
  onCreated,
  onCancel,
  onQuotaExceeded,
}: {
  brandId: number;
  onCreated: (g: XkEventGroup) => void;
  onCancel: () => void;
  /** Fired when the backend rejects with PLAN_LIMIT_EXCEEDED. */
  onQuotaExceeded?: () => void;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [startLocal, setStartLocal] = useState('');
  const [endLocal, setEndLocal] = useState('');

  /*
   * El avanzado del proyecto: sus eventos, sus estaciones y su stock de una vez.
   *
   * Cerrado por defecto — crear un proyecto sigue siendo un nombre y, si
   * acaso, un rango de fechas. Quien lo abre se ahorra montar a mano un árbol
   * que para una gira de cuatro paradas son cuarenta pasos.
   */
  const [avanzado, setAvanzado] = useState(false);
  const [plan, setPlan] = useState<ScaffoldPlan>({
    events: 3,
    stationsPerEvent: 1,
    stock: [],
  });

  const create = useMutation({
    mutationFn: async () => {
      const grupo = await eventsApi.createGroup({
        name: name.trim(),
        companyId: brandId,
        startDate: dateInputToIsoStart(startLocal),
        endDate: dateInputToIsoEnd(endLocal),
      });
      if (!avanzado) return grupo;

      /*
       * Los eventos se reparten por el rango del proyecto, que es lo que
       * acaba de guardarse: el backend parte los días entre los eventos y les
       * da a cada uno su ventana. Por eso esto va DESPUÉS de crear el grupo y
       * no en la misma llamada — sin el rango ya escrito no habría entre qué
       * repartir.
       *
       * Un fallo aquí NO deshace el proyecto: ya existe y es válido. Se avisa
       * de qué parte falló y el usuario decide si reintenta desde la ficha,
       * que es mejor que borrarle un proyecto que sí quería.
       */
      try {
        const r = await eventsApi.scaffold({
          eventGroupId: grupo.id,
          companyId: brandId,
          events: plan.events,
          ...(plan.venue?.address.trim()
            ? {
                address: plan.venue.address.trim(),
                latitude: plan.venue.latitude,
                longitude: plan.venue.longitude,
                placeId: plan.venue.placeId,
              }
            : {}),
          stationsPerEvent: plan.stationsPerEvent,
          stock: plan.stock.filter((l) => l.quantityPerEvent > 0),
        });
        r.notes.forEach((n) => toast({ tone: 'info', title: n }));
        toast({
          tone: 'success',
          title: t('scaffold.built', { events: r.events.length, stations: r.stations }),
        });
      } catch (err) {
        toast({
          tone: 'error',
          title: t('scaffold.projectOnly'),
          description: (err as Error).message,
        });
      }
      return grupo;
    },
    onSuccess: (g) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.eventGroups.byBrand(brandId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.eventGroups.all });
      // El avanzado deja detrás eventos, estaciones y reparto de stock.
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      queryClient.invalidateQueries({ queryKey: ['stock-allocations'] });
      toast({ tone: 'success', title: t('projectForm.created', { name: g.name }) });
      onCreated(g);
    },
    onError: (e: Error) => {
      const payload = (e as { payload?: { code?: string } }).payload;
      if (payload?.code === 'PLAN_LIMIT_EXCEEDED' && onQuotaExceeded) {
        onQuotaExceeded();
        return;
      }
      toast({ tone: 'error', title: t('projectForm.createFailed'), description: e.message });
    },
  });

  const canSubmit = name.trim().length >= 2 && !create.isPending;
  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const invalido = firstInvalidControl(e.currentTarget, t);
    if (invalido) {
      toast({ tone: 'error', title: invalido.message });
      return;
    }
    if (!canSubmit) return;
    const startIso = dateInputToIsoStart(startLocal);
    const endIso = dateInputToIsoEnd(endLocal);
    if (startIso && endIso && new Date(endIso).getTime() <= new Date(startIso).getTime()) {
      toast({ tone: 'error', title: t('projectForm.badRange') });
      return;
    }
    create.mutate();
  };

  return (
    <form noValidate onSubmit={submit} className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <label className="block">
        <div className="mb-1.5 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
          {t('projectForm.name')} <span className="text-[var(--color-accent-ink)]">*</span>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('projectForm.namePlaceholder')}
          autoFocus
          required
          minLength={2}
          className="w-full rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-sm text-[var(--color-xk-lavender)] placeholder:text-[var(--color-xk-text-muted)] focus:border-[var(--color-accent-ink)] focus:outline-none"
        />
      </label>
      <div className="grid grid-cols-1 gap-3">
        <label className="block">
          <div className="mb-1.5 flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            <CalendarClock className="h-3 w-3" />
            {t('projectForm.starts')}
          </div>
          <DatePicker
            mode="date"
            value={startLocal}
            onChange={setStartLocal}
            max={endLocal || undefined}
          />
        </label>
        <label className="block">
          <div className="mb-1.5 flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">
            <CalendarClock className="h-3 w-3" />
            {t('projectForm.ends')}
          </div>
          <DatePicker
            mode="date"
            value={endLocal}
            onChange={setEndLocal}
            min={startLocal || undefined}
          />
        </label>
      </div>
      <p className="rounded-lg border border-dashed border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] px-3 py-2.5 text-[11px] text-[var(--color-xk-text-muted)]">
        <strong className="text-[var(--color-xk-lavender)]">{t('projectForm.optional')}</strong>{' '}
        {t('projectForm.rangeNote')}
      </p>
      <Collapsible
        open={avanzado}
        onOpenChange={setAvanzado}
        label={t('scaffold.advanced')}
        className="rounded-lg border border-[var(--color-xk-border)] p-3"
      >
        <AdvancedScaffold brandId={brandId} plan={plan} onPlan={setPlan} allowEventCount />
      </Collapsible>

      <div className="mt-auto flex flex-wrap items-center justify-end gap-2 border-t border-[var(--color-xk-border)] pt-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[var(--color-xk-border)] px-4 py-2 text-xs font-bold text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]"
        >
          {t('stock.dialog.cancel')}
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-xk-accent)] px-5 py-2 text-xs font-extrabold text-[var(--color-fg-on-accent)] transition-colors hover:bg-[var(--color-xk-accent-hover)] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {create.isPending ? t('projectForm.creating') : t('projectForm.create')}
        </button>
      </div>
    </form>
  );
}
